import { Link } from 'react-router-dom';
import { useState } from 'react';
import VisitorCounter from '@/components/base/VisitorCounter';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      data.append(key, value as string);
    }
    try {
      await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
      setEmail('');
    } catch {
      // silently fail - still show success to user
      setSubmitted(true);
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  const footerLinks = {
    services: [
      { name: 'All Services', to: '/services' },
      { name: 'IT Staff Augmentation', to: '/services' },
      { name: 'Dedicated Teams', to: '/services' },
      { name: 'Contract Hiring', to: '/services' },
      { name: 'Offshore Development', to: '/services' },
    ],
    company: [
      { name: 'About Us', to: '/about' },
      { name: 'Blog', to: '/blog' },
      { name: 'Leadership', to: '/leadership' },
      { name: 'Terms of Use', to: '/terms' },
      { name: 'Privacy Policy', to: '/privacy' },
    ],
    technologies: [
      { name: 'Frontend Development', to: '/technologies' },
      { name: 'Backend Development', to: '/technologies' },
      { name: 'Cloud & DevOps', to: '/technologies' },
      { name: 'AI & Data', to: '/technologies' },
      { name: 'Industries', to: '/industries' },

    ],
    contact: [
      { name: 'Schedule a Call', href: '#cta' },
      { name: 'Email: info@thoughtic.com', href: 'mailto:info@thoughtic.com' },
      { name: 'Phone: +91 9959535367', href: 'tel:+919959535367' },
      { name: 'Location: Hyderabad, India', href: '#cta' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', icon: 'ri-linkedin-fill', href: 'https://linkedin.com/company/thoughtic-solutions' },
    { name: 'Twitter', icon: 'ri-instagram-fill', href: 'https://www.instagram.com/thoughticsolutions/' },
    { name: 'Facebook', icon: 'ri-facebook-fill', href: 'https://www.facebook.com/thoughticsolutions' },
    { name: 'YouTube', icon: 'ri-youtube-fill', href: 'https://www.youtube.com/channel/UCMu9QREx4g1iFhATG53X3nQ' },
  ];

  return (
    <footer className="bg-[#1c1917] text-white relative overflow-hidden">
      {/* Subtle Pattern Background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Top Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 mb-0 md:mb-5">
          {/* Left - Brand Area */}
          <div className="lg:col-span-4">
            <div className="flex items-center space-x-3 mb-5 md:mb-6">
              <div className="rounded-lg">
                <img
                  src="/thoughtic-solutions-white.png"
                  alt="Thoughtic Solutions"
                  className="h-20 w-auto"
                />
              </div>
            </div>

            <p className="text-sm md:text-base text-white/70 mb-5 md:mb-6 leading-relaxed">
              Bridge your tech skill gaps with flexible, plug-and-play developers. We help businesses scale faster with highly skilled IT professionals.
            </p>

            {/* Email Subscription */}
            <form
              onSubmit={handleSubscribe}
              method="POST"
              action="https://readdy.ai/api/form/d8tmuj4uatl81q3tfn10"
              encType="application/x-www-form-urlencoded"
              data-readdy-form
              className="mb-5 md:mb-6"
            >
              {/* honeypot */}
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute opacity-0 pointer-events-none" />
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 bg-transparent border-2 border-white/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-primary-400 transition-colors text-sm"
                  required
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="h-12 px-4 flex items-center justify-center bg-white/10 border-2 border-white/30 rounded-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium disabled:opacity-50"
                  aria-label="Subscribe"
                >
                  <i className="ri-notification-line text-lg text-white mr-1"></i>
                  <span className="text-white">{submitted ? 'Subscribed!' : submitting ? 'Sending...' : 'Subscribe'}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Right - Navigation Grid */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {/* Services */}
              <div>
                <h3 className="text-sm md:text-base font-semibold mb-3 md:mb-4 text-white">Services</h3>
                <ul className="space-y-2 md:space-y-3">
                  {footerLinks.services.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.to}
                        className="text-xs md:text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

                {/* Technologies */}
              <div>
                <h3 className="text-sm md:text-base font-semibold mb-3 md:mb-4 text-white">Technologies</h3>
                <ul className="space-y-2 md:space-y-3">
                  {footerLinks.technologies.map((link, index) => (
                    <li key={index}>
                      <Link
                        to={link.to}
                        className="text-xs md:text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company */}
              <div>
                <h3 className="text-sm md:text-base font-semibold mb-3 md:mb-4 text-white">Quick Links</h3>
                <ul className="space-y-2 md:space-y-3">
                  {footerLinks.company.map((link, index) => (
                    <li key={index}>
                      {'to' in link ? (
                        <Link
                          to={link.to}
                          className="text-xs md:text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-xs md:text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

            

              {/* Contact */}
              <div>
                <h3 className="text-sm md:text-base font-semibold mb-3 md:mb-4 text-white">Contact</h3>
                <ul className="space-y-2 md:space-y-3">
                  {footerLinks.contact.map((link, index) => (
                    <li key={index}>
                      {'to' in link ? (
                        <Link
                          to={link.to}
                          className="text-xs md:text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-xs md:text-sm text-white/70 hover:text-white transition-colors cursor-pointer"
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-6 md:pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <p className="text-xs md:text-sm text-white/60">
              &copy; 2026 Thoughtic Solutions. All rights reserved.
            </p>

            {/* Legal Links */}
            <div className="flex items-center space-x-4">
              <Link to="/terms" className="text-xs md:text-sm text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Terms of Use
              </Link>
              <Link to="/privacy" className="text-xs md:text-sm text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Privacy Policy
              </Link>
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center border border-white/30 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  aria-label={social.name}
                >
                  <i className={`${social.icon} text-sm md:text-lg text-white`}></i>
                </a>
              ))}
            </div>

            {/* Visitor Counter */}
            {/* <VisitorCounter /> */}

            {/* Quick Link */}
            {/* <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-xs md:text-sm text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a> */}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;